import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type DashboardParams = {
  fromDate?: Date;
  toDate?: Date;
  branchId?: string;
};

export type DashboardKPI = {
  todaySalesLak: number;
  todayOrdersCount: number;
  totalWalletLak: number;
  pendingOrdersCount: number;
  lowStockItemCount: number;
};

export type SalesOverTimeItem = {
  period: string;
  sales: number;
};

export type SalesByBranchItem = {
  branch: string;
  sales: number;
};

export type OrdersByStatusItem = {
  status: string;
  pending: number;
  processing: number;
  completed: number;
  cancelled: number;
};

export type DashboardCharts = {
  salesOverTimeData: SalesOverTimeItem[];
  salesByBranchData: SalesByBranchItem[];
  ordersByStatusData: OrdersByStatusItem[];
};

export type TopProduct = {
  id: string;
  name: string;
  qtySold: number;
  totalSalesLak: number;
};

export type RecentOrder = {
  id: string;
  orderNo: string;
  branchName: string;
  status: string;
  totalLak: number;
  createdAt: Date;
};

export type WalletTransaction = {
  id: string;
  type: string;
  amountLak: number;
  customerName?: string;
  createdAt: Date;
};

export type DashboardTables = {
  topProducts: TopProduct[];
  recentOrders: RecentOrder[];
  walletTransactions: WalletTransaction[];
};

export type ExpiringLot = {
  id: string;
  lotNo: string;
  itemName: string;
  branchName?: string;
  expiryDate?: Date;
  daysUntilExpiry?: number | null;
  qtyRemaining: number;
  valueLak: number;
};

export type PendingApproval = {
  id: string;
  type: "topup" | "refund";
  amountLak: number;
  customerName?: string;
  requestedAt: Date;
};

export type LowStockAlertItem = {
  itemId: string;
  itemName: string;
  branchCode: string;
  onHand: number;
  minQty: number;
  unit: string;
};

export type DashboardAlerts = {
  expiringLots: ExpiringLot[];
  pendingApprovals: PendingApproval[];
  pendingTopupCount: number;
  pendingRefundCount: number;
  lowStockItemCount: number;
  lowStockItems: LowStockAlertItem[];
};

export const dashboardApi = {
  // KPI Data
  async getKPIData(params?: DashboardParams): Promise<DashboardKPI> {
    const searchParams = new URLSearchParams();
    if (params?.fromDate) {
      searchParams.set("fromDate", params.fromDate.toISOString());
    }
    if (params?.toDate) {
      searchParams.set("toDate", params.toDate.toISOString());
    }
    if (params?.branchId) {
      searchParams.set("branchId", params.branchId);
    }

    return fetcher.get<DashboardKPI>(
      `${config.apiUrl}/dashboard/kpi?${searchParams.toString()}`,
    );
  },

  // Chart Data
  async getChartData(params?: DashboardParams): Promise<DashboardCharts> {
    const searchParams = new URLSearchParams();
    if (params?.fromDate) {
      searchParams.set("fromDate", params.fromDate.toISOString());
    }
    if (params?.toDate) {
      searchParams.set("toDate", params.toDate.toISOString());
    }
    if (params?.branchId) {
      searchParams.set("branchId", params.branchId);
    }

    return fetcher.get<DashboardCharts>(
      `${config.apiUrl}/dashboard/charts?${searchParams.toString()}`,
    );
  },

  // Table Data
  async getTableData(params?: DashboardParams): Promise<DashboardTables> {
    const searchParams = new URLSearchParams();
    if (params?.fromDate) {
      searchParams.set("fromDate", params.fromDate.toISOString());
    }
    if (params?.toDate) {
      searchParams.set("toDate", params.toDate.toISOString());
    }
    if (params?.branchId) {
      searchParams.set("branchId", params.branchId);
    }

    return fetcher.get<DashboardTables>(
      `${config.apiUrl}/dashboard/tables?${searchParams.toString()}`,
    );
  },

  // Alert Data
  async getAlertData(params?: DashboardParams): Promise<DashboardAlerts> {
    const searchParams = new URLSearchParams();
    if (params?.branchId) {
      searchParams.set("branchId", params.branchId);
    }

    return fetcher.get<DashboardAlerts>(
      `${config.apiUrl}/dashboard/alerts?${searchParams.toString()}`,
    );
  },
};
